import { useQuery, useMutation, useQueryClient } from "react-query";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";

export interface PairMapperItem {
  _id: string;
  name: string;
  status: string;
  matchedBy: string;
  matchConfidence?: number;
  keywordOverlap?: string[];
  expiryDate: string;
  category?: string;
  subcategory?: string;
  siteA: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  siteB: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  createdAt: string;
}

interface PairsResponse {
  pairs: PairMapperItem[];
  total: number;
}

interface PairStats {
  total: number;
  active: number;
  pending: number;
  keywordPending: number;
  expired: number;
}

export function usePairs(params: {
  status?: string;
  matchedBy?: string;
  search?: string;
  category?: string;
  subcategory?: string;
  runId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery(
    ["prediction-pairs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairsResponse>(
        "/pairs/all",
        { params },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export function usePairStats() {
  return useQuery(["prediction-pair-stats"], async () => {
    const { data } =
      await predictionManagerAxiosApi.get<PairStats>("/pairs/stats");
    return data;
  });
}

export interface PairStatsByCategory {
  total: number;
  byCategory: Record<
    string,
    { total: number; bySubcategory: Record<string, number> }
  >;
}

// Feeds the count badges on the mapper page's category tabs + subcategory
// chips. When `runId` is passed, counts are restricted to pairs created by
// that matching run; otherwise it aggregates across all pairs.
export function usePairStatsByCategory(runId?: string | null) {
  return useQuery<PairStatsByCategory>(
    ["prediction-pair-stats-by-category", runId ?? null],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairStatsByCategory>(
        "/pairs/stats/by-category",
        { params: runId ? { runId } : {} },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export function useConfirmPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/confirm`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useManualConfirmPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/manual-confirm`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useRejectPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/reject`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useFetchMarkets() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post("/matching/fetch");
      return data;
    },
    {
      // The Lambda fetch is fire-and-forget — the only visible result is a new
      // entry in `prediction_matching_runs` after each platform's fetch lands.
      // Invalidate the runs query so the History tab reflects it on next view.
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("matching-runs-summary");
      },
    },
  );
}

// Trigger the dedicated crypto 15-min up/down matcher (scoped fetch + the
// deterministic crypto:coin match). Single lambda invocation; results land in
// `prediction_matching_runs` once the run completes (~3 min).
export function useTriggerCryptoMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (params: { horizonMinutes?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.horizonMinutes != null) {
        qs.set("horizonMinutes", String(params.horizonMinutes));
      }
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/crypto-match${qs.toString() ? `?${qs}` : ""}`,
      );
      return data as { invoked: boolean; message: string };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("matching-runs-summary");
      },
    },
  );
}

// Trigger the daily-temp matcher (climate:daily_high_temp + daily_low_temp).
// 17 US cities across both platforms. Single lambda invocation; results land
// in `prediction_matching_runs` once the run completes (~3 min).
export function useTriggerDailyTempMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (params: { targetDate?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.targetDate) qs.set("targetDate", params.targetDate);
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/daily-temp-match${qs.toString() ? `?${qs}` : ""}`,
      );
      return data as { invoked: boolean; message: string };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("matching-runs-summary");
      },
    },
  );
}

export interface RunMatchingOptions {
  fullRun?: boolean;
  skipFetch?: boolean;
  tiers?: string; // e.g. "deterministic", "deterministic,ai_confirmed", "keyword_fallback"
}

export function useRunMatching(defaultOptions: RunMatchingOptions = {}) {
  const qc = useQueryClient();
  return useMutation(
    async (overrides?: RunMatchingOptions) => {
      const opts = { ...defaultOptions, ...overrides };
      const params = new URLSearchParams();
      if (opts.fullRun) params.set("fullRun", "true");
      if (opts.skipFetch) params.set("skipFetch", "true");
      if (opts.tiers) params.set("tiers", opts.tiers);
      const qs = params.toString();
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/run${qs ? `?${qs}` : ""}`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("matching-runs");
      },
    },
  );
}

export interface MatcherScheduleState {
  polymarket: { ruleName: string; state: 'ENABLED' | 'DISABLED' | 'UNKNOWN' };
  kalshi: { ruleName: string; state: 'ENABLED' | 'DISABLED' | 'UNKNOWN' };
  enabled: boolean;
}

export function useMatcherSchedule() {
  return useQuery<MatcherScheduleState>(
    'matcher-schedule-state',
    async () => {
      const { data } = await predictionManagerAxiosApi.get<MatcherScheduleState>(
        '/matching/schedule',
      );
      return data;
    },
    {
      refetchInterval: 30_000,
      staleTime: 15_000,
    },
  );
}

export function useToggleMatcherSchedule() {
  const qc = useQueryClient();
  return useMutation(
    async (nextEnabled: boolean) => {
      const path = nextEnabled
        ? '/matching/schedule/enable'
        : '/matching/schedule/disable';
      const { data } = await predictionManagerAxiosApi.post<MatcherScheduleState>(
        path,
      );
      return data;
    },
    {
      // Optimistic UI: flip immediately, let the response confirm.
      onMutate: async (nextEnabled) => {
        await qc.cancelQueries('matcher-schedule-state');
        const prev = qc.getQueryData<MatcherScheduleState>(
          'matcher-schedule-state',
        );
        if (prev) {
          qc.setQueryData<MatcherScheduleState>('matcher-schedule-state', {
            ...prev,
            enabled: nextEnabled,
          });
        }
        return { prev };
      },
      onError: (_err, _next, ctx: any) => {
        if (ctx?.prev) qc.setQueryData('matcher-schedule-state', ctx.prev);
      },
      onSettled: (data) => {
        if (data) qc.setQueryData('matcher-schedule-state', data);
        else qc.invalidateQueries('matcher-schedule-state');
      },
    },
  );
}

// ─── Per-rule matching schedules (replaces the single Auto-run toggle) ──
//
// The prediction-manager now exposes four independent schedule rules instead
// of one binary matcher toggle. `useMatcherSchedule`/`useToggleMatcherSchedule`
// above are kept for one release while consumers migrate to these.
export type ScheduleRuleName = 'polymarket' | 'kalshi' | 'crypto' | 'dailyTemp';

export interface ScheduleRuleState {
  name: ScheduleRuleName;
  ruleName: string;
  kind: 'hourly' | 'deterministic';
  state: 'ENABLED' | 'DISABLED' | 'UNKNOWN';
  running: boolean;
}

export interface ScheduleState {
  rules: ScheduleRuleState[];
  concurrency: {
    polymarket: number | null;
    kalshi: number | null;
    deterministic: number | null;
  };
}

export function useSchedules() {
  return useQuery<ScheduleState>(
    'matcher-schedules-state',
    async () =>
      (
        await predictionManagerAxiosApi.get<ScheduleState>(
          '/matching/schedules',
        )
      ).data,
    {
      refetchInterval: 30_000,
      staleTime: 15_000,
    },
  );
}

export function useToggleSchedule() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      name,
      enabled,
    }: {
      name: ScheduleRuleName;
      enabled: boolean;
    }) => {
      const { data } = await predictionManagerAxiosApi.post<ScheduleState>(
        `/matching/schedules/${name}/${enabled ? 'enable' : 'disable'}`,
      );
      return data;
    },
    {
      // Optimistic UI: flip ONLY the targeted rule, leave the rest untouched.
      onMutate: async ({ name, enabled }) => {
        await qc.cancelQueries('matcher-schedules-state');
        const prev = qc.getQueryData<ScheduleState>('matcher-schedules-state');
        if (prev) {
          qc.setQueryData<ScheduleState>('matcher-schedules-state', {
            ...prev,
            rules: prev.rules.map((r) =>
              r.name === name
                ? {
                    ...r,
                    state: enabled ? 'ENABLED' : 'DISABLED',
                    running: enabled,
                  }
                : r,
            ),
          });
        }
        return { prev };
      },
      onError: (_err, _vars, ctx: any) => {
        if (ctx?.prev)
          qc.setQueryData('matcher-schedules-state', ctx.prev);
      },
      onSettled: (data) => {
        if (data) qc.setQueryData('matcher-schedules-state', data);
        else qc.invalidateQueries('matcher-schedules-state');
      },
    },
  );
}

export function useBackfillKeywords() {
  return useMutation(async () => {
    const { data } = await predictionManagerAxiosApi.post(
      "/matching/backfill-keywords",
    );
    return data;
  });
}

export interface CachedMarketResult {
  _id: string;
  platform: string;
  externalId: string;
  title: string;
  eventTitle: string;
  keywords: string[];
  isPaired: boolean;
  endDate: string;
  effectiveEndDate?: string;
}

export function useSearchMarkets(
  q: string,
  platform?: string,
  enabled = true,
) {
  return useQuery(
    ["search-markets", q, platform],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        CachedMarketResult[]
      >("/pairs/search-markets", {
        params: { q, platform, limit: 20 },
      });
      return data;
    },
    { enabled: enabled && q.length >= 2, keepPreviousData: true },
  );
}

// ─── Matching Run History ────────────────────────────────────────────

export interface MatchDecisionItem {
  sourceExternalId: string;
  sourcePlatform: string;
  sourceTitle: string;
  outcome: "matched" | "no_match" | "no_candidates" | "error";
  matchedExternalId?: string;
  matchedPlatform?: string;
  matchedTitle?: string;
  confidence?: number;
  reasoning?: string;
  keywordOverlap?: string[];
  pairId?: string;
  candidatesConsidered: number;
  errorMessage?: string;
}

export interface MatchingRunItem {
  _id: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  trigger: string;
  // Which matcher this run belongs to. Absent on legacy / local runs.
  platform?: "polymarket" | "kalshi";
  // Markets ready to match (unpaired + enriched) — NOT what the fetch returned.
  newMarketsPolymarket: number;
  newMarketsKalshi: number;
  // Markets pulled from the platform API this run + the first-seen-new subset.
  // Absent on runs created before this field existed → render "—".
  marketsFetched?: number;
  newMarketsFetched?: number;
  matchesFound: number;
  matchesCreated: number;
  keywordsExtracted: number;
  errors: string[];
  durationMs: number;
  decisions?: MatchDecisionItem[];
}

export interface MatchingRunSummary {
  windowHours: number;
  runCount: number;
  completed: number;
  failed: number;
  totalMarketsFetched: number;
  totalMarketsFetchedPolymarket: number;
  totalMarketsFetchedKalshi: number;
  totalNewMarketsFetched: number;
  totalNewMarketsFetchedPolymarket: number;
  totalNewMarketsFetchedKalshi: number;
  totalPairsCreated: number;
  totalErrors: number;
}

// Aggregate roll-up across matching runs in a recent window — feeds the
// summary card at the top of the mapper page's History tab. Per-bucket runs
// from run-buckets-sequential.ts are excluded by default so a 62-bucket
// session doesn't get double-counted.
export function useMatchingRunSummary(sinceHours: number) {
  return useQuery<MatchingRunSummary>(
    ["matching-run-summary", sinceHours],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<MatchingRunSummary>(
        "/matching/runs/summary",
        { params: { sinceHours } },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

// Standing snapshot of the matcher corpus — counts of cached markets per
// platform and active pairs per matched-by source. Independent of any time
// window; reflects current DB state. Feeds the "Corpus" panel on the
// History tab.
export interface CorpusSnapshot {
  cachedMarkets: { total: number; polymarket: number; kalshi: number };
  activePairs: {
    total: number;
    byMatchedBy: {
      manual: number;
      deterministic: number;
      semantic_auto: number;
      semantic_fallback: number;
    };
  };
}

export function useCorpusSnapshot() {
  return useQuery<CorpusSnapshot>(
    ["matching-corpus"],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<CorpusSnapshot>(
        "/matching/corpus",
      );
      return data;
    },
    { keepPreviousData: true, refetchInterval: 60_000 },
  );
}

export function useMatchingRuns(params: { limit?: number; offset?: number }) {
  return useQuery(
    ["matching-runs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<{
        runs: MatchingRunItem[];
        total: number;
      }>("/matching/runs", { params });
      return data;
    },
    { keepPreviousData: true },
  );
}

// Watches the recent runs feed for new run docs whose `marketsFetched` has been
// populated by the lambda after a Fetch click. Returns the most recent matching
// run per platform + a `done` flag once BOTH platforms have reported.
//
// `baselineMs` is the unix-ms timestamp captured at click time — runs whose
// `startedAt` is older than that are ignored (they belong to a prior fetch).
// Polling stops automatically once the caller clears the baseline (passes null).
export function useFetchProgress(baselineMs: number | null): {
  polymarket: MatchingRunItem | null;
  kalshi: MatchingRunItem | null;
  done: boolean;
} {
  const enabled = baselineMs !== null;
  const { data } = useQuery(
    ["matching-runs", "fetch-progress"],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<{
        runs: MatchingRunItem[];
        total: number;
      }>("/matching/runs", { params: { limit: 10 } });
      return data;
    },
    {
      enabled,
      // Poll every 3s while the caller is watching; React Query handles the
      // stop automatically once `enabled` flips back to false.
      refetchInterval: enabled ? 3000 : false,
    },
  );

  if (!enabled || !data) return { polymarket: null, kalshi: null, done: false };

  const newer = (data.runs || []).filter(
    (r) => new Date(r.startedAt).getTime() >= (baselineMs as number),
  );
  // First (most-recent-by-startedAt — the runs feed is desc) populated run per platform.
  const polymarket =
    newer.find((r) => r.platform === "polymarket" && r.marketsFetched != null) ??
    null;
  const kalshi =
    newer.find((r) => r.platform === "kalshi" && r.marketsFetched != null) ??
    null;
  return { polymarket, kalshi, done: !!polymarket && !!kalshi };
}

export function useMatchingRunDetail(runId: string | null) {
  return useQuery(
    ["matching-run-detail", runId],
    async () => {
      const { data } =
        await predictionManagerAxiosApi.get<MatchingRunItem>(
          `/matching/runs/${runId}`,
        );
      return data;
    },
    { enabled: !!runId },
  );
}

export function useRetryErrors() {
  const qc = useQueryClient();
  return useMutation(
    async (runId: string) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/runs/${runId}/retry-errors`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useManualPair() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { polymarketId: string; kalshiId: string }) => {
      const { data } = await predictionManagerAxiosApi.post(
        "/pairs/manual-pair",
        body,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("search-markets");
      },
    },
  );
}
